"use client";

import type { MagnusUIMessage } from "@magnus/contract/messages";
import { isSdkUploadProcessingTerminal } from "@magnus/contract/uploads";
import type { ProcessingStatus } from "@magnus/contract/values";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { MagnusUploadsSource } from "./source-stores";

/** One sent attachment's processing state, keyed by upload id in the map
 *  the block's `attachmentStatuses` prop takes. */
export interface MagnusAttachmentProcessingSnapshot {
  richPassFailed: boolean | null;
  status: ProcessingStatus | null;
}

/** The slice of an uploads source the live statuses need. Both methods are
 *  optional so a host's own uploads seam can opt in piecemeal. */
export type MagnusAttachmentStatusesSource = Partial<Pick<MagnusUploadsSource, "retryProcessing" | "watchProcessing">>;
export interface UseMagnusAttachmentStatusesResult {
  /** Live snapshots for every sent attachment, keyed by upload id. Holds
   *  its identity while nothing changed, so memoized rows stay put while a
   *  reply streams. */
  attachmentStatuses: ReadonlyMap<string, MagnusAttachmentProcessingSnapshot>;
  /** Retry a failed sent attachment; `undefined` when the source can't. */
  onRetryProcessing?: (uploadId: string) => void;
}
type PartData = {
  processingStatus?: ProcessingStatus | null;
  richPassFailed?: boolean | null;
  uploadId?: string;
};

/** Most uploads watched at once. A chat reopened with dozens of sent
 *  documents must not fire dozens of concurrent polls; the rest wait for a
 *  slot, newest first. */
const MAX_CONCURRENT_ATTACHMENT_WATCHES = 4;

/** Non-terminal reports a historic upload gets before its watch is parked
 *  (~1 min at the source's 3 s cadence). A historic row still mid-pipeline
 *  is almost always stuck, and polling it forever would hold a slot. */
const HISTORIC_WATCH_MAX_REPORTS = 20;

/** Statuses past the text gate: the document is usable, only the rich
 *  pass / indexing may still run. For a historic message the send-time
 *  snapshot is stale and the pass long done, so they read as settled. */
const DISPLAY_SETTLED_STATUSES: ReadonlySet<ProcessingStatus> = new Set(["chunks-ready", "text-ready"]);

/** A gone upload (404) reads as settled: no dot, no retry that would 404. */
const GONE_SNAPSHOT: MagnusAttachmentProcessingSnapshot = {
  richPassFailed: null,
  status: "completed"
};
interface SeededAttachments {
  /** Upload ids newest message first (watch priority). */
  order: string[];
  /** Ids on the latest message that carries attachments — the only ones
   *  that can still be in flight from this session's send. */
  recent: ReadonlySet<string>;
  signature: string;
  snapshots: Map<string, MagnusAttachmentProcessingSnapshot>;
}
function attachmentDataOf(message: MagnusUIMessage): PartData[] {
  const found: PartData[] = [];
  for (const part of message.parts ?? []) {
    if (part.type !== "file" && part.type !== "data-magnus-document") {
      continue;
    }
    const data = (part as {
      data?: PartData;
    }).data;
    if (data?.uploadId) {
      found.push(data);
    }
  }
  return found;
}

/** The send-time snapshot each attachment part carries on its `data`
 *  (native `file` parts from the composer, `data-magnus-document` parts
 *  from the server's document hydration). A historic attachment (not on
 *  the latest attachment-bearing message) past the text gate reads as
 *  `completed`: its stored snapshot is stale, and polling every one of
 *  them just to learn that is what reopening a long chat must not do. */
function collectSnapshots(messages: readonly MagnusUIMessage[]): Omit<SeededAttachments, "signature"> {
  const snapshots = new Map<string, MagnusAttachmentProcessingSnapshot>();
  const order: string[] = [];
  let recent: ReadonlySet<string> | undefined;
  for (let i = messages.length - 1; i >= 0; i--) {
    const parts = attachmentDataOf(messages[i] as MagnusUIMessage);
    if (parts.length === 0) {
      continue;
    }
    const isRecent = recent === undefined;
    if (isRecent) {
      recent = new Set(parts.map(data => data.uploadId as string));
    }
    for (const data of parts) {
      const id = data.uploadId as string;
      // Newest occurrence wins, matching a forward scan's last write.
      if (snapshots.has(id)) {
        continue;
      }
      const status = data.processingStatus ?? null;
      const richPassFailed = data.richPassFailed ?? null;
      const settled = !(isRecent || richPassFailed === true) && status !== null && DISPLAY_SETTLED_STATUSES.has(status);
      snapshots.set(id, {
        richPassFailed,
        status: settled ? "completed" : status
      });
      order.push(id);
    }
  }
  return {
    order,
    recent: recent ?? new Set(),
    snapshots
  };
}

/** Order-independent content signature, so a fresh `messages` array with
 *  the same uploads in the same states maps to the same cached Map. */
function signatureOf(seeded: Omit<SeededAttachments, "signature">): string {
  const entries = [...seeded.snapshots.entries()].sort(([a], [b]) => a.localeCompare(b)).map(([id, s]) => `${id}:${s.status ?? ""}:${s.richPassFailed ?? ""}`).join("|");
  return `${entries}#${seeded.order.join("|")}`;
}
function withAdded(previous: ReadonlySet<string>, id: string): ReadonlySet<string> {
  if (previous.has(id)) {
    return previous;
  }
  const next = new Set(previous);
  next.add(id);
  return next;
}
function withRemoved(previous: ReadonlySet<string>, id: string): ReadonlySet<string> {
  if (!previous.has(id)) {
    return previous;
  }
  const next = new Set(previous);
  next.delete(id);
  return next;
}

/**
 * Live processing dots + retry for attachments already sent in a chat
 * (plan 043 P8, gap-audit T4/T17) — the SDK port of the flagship's tRPC
 * `useChatProcessingStatuses`. Seeds from the send-time snapshot on each
 * attachment part, watches non-terminal uploads through the source's
 * `watchProcessing` (the same poll the composer's staged tiles use), and
 * retries a failed one through `retryProcessing`, resuming the watch once
 * the server accepts.
 *
 * Reopening a long chat stays cheap: historic attachments past the text
 * gate (`text-ready`, `chunks-ready`) read as settled without a poll; at
 * most {@link MAX_CONCURRENT_ATTACHMENT_WATCHES} watches run at once
 * (retries, then the latest message, then newest first); a historic watch
 * that never settles is parked after about a minute; and an upload the
 * server no longer has (a `gone` report) reads as settled, with no retry.
 *
 * The block calls this itself with the `uploads` source when the host
 * passes no `attachmentStatuses`, so spreading
 * `useMagnusConversation().chatProps` lights it up. Pass `undefined` as the
 * source to turn it off (no watches; the seeded map still comes back).
 *
 * `source.watchProcessing` must keep its identity across renders: a new
 * function is read as a new source, so every watch is dropped and
 * re-subscribed against it. The source object itself may be fresh each
 * render (an inline `uploads={{ ...uploads }}` is fine); an inline
 * `watchProcessing: (id, cb) => …` re-subscribes on every parent render.
 */
export function useMagnusAttachmentStatuses(messages: readonly MagnusUIMessage[], source: MagnusAttachmentStatusesSource | undefined): UseMagnusAttachmentStatusesResult {
  const watch = source?.watchProcessing;
  const retry = source?.retryProcessing;

  // The stream hands over a new `messages` array per token; returning a
  // fresh Map each time would re-render every memoized row on every token.
  // Scan each render (cheap) but reuse the cached result while the
  // signature holds.
  const seededRef = useRef<SeededAttachments | null>(null);
  const seededAttachments = useMemo(() => {
    const collected = collectSnapshots(messages);
    const signature = signatureOf(collected);
    if (seededRef.current?.signature === signature) {
      return seededRef.current;
    }
    seededRef.current = {
      ...collected,
      signature
    };
    return seededRef.current;
  }, [messages]);
  const {
    order,
    recent,
    snapshots: seeded
  } = seededAttachments;
  const [live, setLive] = useState<ReadonlyMap<string, MagnusAttachmentProcessingSnapshot>>(() => new Map());
  // Ids the server accepted a retry for: watched again even though their
  // last known state is terminal (the failure being retried).
  const [retried, setRetried] = useState<ReadonlySet<string>>(() => new Set());
  // Historic ids whose watch ran out of reports without settling.
  const [parked, setParked] = useState<ReadonlySet<string>>(() => new Set());

  // Read inside watch callbacks, which outlive the render that made them.
  const recentRef = useRef(recent);
  recentRef.current = recent;
  const retriedRef = useRef(retried);
  retriedRef.current = retried;
  const reportCountsRef = useRef(new Map<string, number>());

  // An upload needs a watch while its freshest known state is non-terminal,
  // or while a retry is pending its first report. Only the first few (by
  // priority) hold a slot.
  const watchIds = useMemo(() => {
    if (!watch) {
      return [];
    }
    const priority = (id: string) => {
      if (retried.has(id)) {
        return 0;
      }
      return recent.has(id) ? 1 : 2;
    };
    const candidates: string[] = [];
    for (const id_0 of order) {
      const latest = live.get(id_0) ?? seeded.get(id_0);
      if (retried.has(id_0)) {
        candidates.push(id_0);
      } else if (!(parked.has(id_0) || isSdkUploadProcessingTerminal(latest))) {
        candidates.push(id_0);
      }
    }
    // Stable sort keeps newest-first within a priority band.
    candidates.sort((a, b) => priority(a) - priority(b));
    return candidates.slice(0, MAX_CONCURRENT_ATTACHMENT_WATCHES).sort();
  }, [live, order, parked, recent, retried, seeded, watch]);
  const watchKey = watchIds.join("|");
  const subscriptionsRef = useRef(new Map<string, () => void>());

  // A new source (another chat, a remounted store) drops every watch; the
  // effect below re-subscribes the still-needed ones against it. Declared
  // first so its cleanup runs before the diff effect re-subscribes.
  // biome-ignore lint/correctness/useExhaustiveDependencies: `watch` is the trigger — a new source must drop the old source's watches
  useEffect(() => {
    const subscriptions = subscriptionsRef.current;
    return () => {
      for (const unsubscribe of subscriptions.values()) {
        unsubscribe();
      }
      subscriptions.clear();
    };
  }, [watch]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: keyed on the id list, not the per-render array identity
  useEffect(() => {
    if (!watch) {
      return;
    }
    const subscriptions_0 = subscriptionsRef.current;
    const wanted = new Set(watchIds);
    for (const [id_1, unsubscribe_0] of subscriptions_0) {
      if (!wanted.has(id_1)) {
        unsubscribe_0();
        subscriptions_0.delete(id_1);
      }
    }
    for (const id_2 of watchIds) {
      if (subscriptions_0.has(id_2)) {
        continue;
      }
      // A source may report from inside `watch()` itself; a terminal report
      // there must not be followed by registering the finished watch.
      let finished = false;
      const unsubscribe_1 = watch(id_2, update => {
        const snapshot: MagnusAttachmentProcessingSnapshot = update.gone ? GONE_SNAPSHOT : {
          richPassFailed: update.richPassFailed ?? null,
          status: update.status ?? null
        };
        setLive(previous => {
          const next = new Map(previous);
          next.set(id_2, snapshot);
          return next;
        });
        if (update.terminal) {
          // The watch stops itself on a terminal report; forget it so a
          // later retry can subscribe afresh.
          finished = true;
          subscriptions_0.delete(id_2);
          reportCountsRef.current.delete(id_2);
          setRetried(previous_0 => withRemoved(previous_0, id_2));
          return;
        }
        const historic = !(recentRef.current.has(id_2) || retriedRef.current.has(id_2));
        if (!historic) {
          return;
        }
        const reports = (reportCountsRef.current.get(id_2) ?? 0) + 1;
        reportCountsRef.current.set(id_2, reports);
        if (reports >= HISTORIC_WATCH_MAX_REPORTS) {
          // Park it: the diff effect unsubscribes it and frees the slot.
          setParked(previous_1 => withAdded(previous_1, id_2));
        }
      });
      if (!finished) {
        subscriptions_0.set(id_2, unsubscribe_1);
      }
    }
  }, [watchKey, watch]);
  const attachmentStatuses = useMemo(() => {
    // No live reports yet: hand back the (reference-stable) seeded map so
    // the common case — nothing in flight — never mints a new identity.
    if (live.size === 0) {
      return seeded;
    }
    const merged = new Map(seeded);
    for (const [id_3, snapshot_0] of live) {
      if (merged.has(id_3)) {
        merged.set(id_3, snapshot_0);
      }
    }
    return merged;
  }, [live, seeded]);
  const onRetryProcessing = useCallback((uploadId: string) => {
    if (!retry) {
      return;
    }
    // Resume the watch only once the server has reset the row: watching
    // first could read the old failure back and stop again.
    retry(uploadId).then(() => {
      // A retried upload gets a fresh report budget.
      reportCountsRef.current.delete(uploadId);
      setParked(previous_2 => withRemoved(previous_2, uploadId));
      setRetried(previous_3 => withAdded(previous_3, uploadId));
    }, (err: unknown) => {
      console.error("[magnus] retryProcessing failed", err);
    });
  }, [retry]);
  return useMemo(() => ({
    attachmentStatuses,
    onRetryProcessing: retry ? onRetryProcessing : undefined
  }), [attachmentStatuses, onRetryProcessing, retry]);
}